// Composer Studio — 표 셀 클립보드(복사/잘라내기/붙여넣기) 공용 순수 함수.
//
// 두 채널로 동작한다:
//   1) 값(TSV) — 엑셀/Google Sheets 호환 표준(열 구분 `\t`, 행 구분 `\n`). clipboard 이벤트의
//      `clipboardData`(권한 불필요)에 setData/getData로 주고받는다. 외부(Excel)는 이 값만 인식.
//   2) full-fidelity 스냅샷 — 값+서식+구조를 담은 CellClipboardData를 localStorage(`composer:cell-clipboard`)에
//      실어 같은 origin의 탭/창 간 셀 서브범위를 온전히 이동한다(내부 크로스탭 복사). 붙여넣기 text가
//      스냅샷 tsv와 일치하면 내부로 보고 full 적용, 아니면 외부 TSV(값만)로 처리한다.
//
// 여기서는 셀 데이터 ↔ TSV/스냅샷 직렬화·역직렬화만 담당한다(이벤트/localStorage 배선은 EditorPage).

import type { TableCell, TableNode } from '@safetyhub/shared';
import type { CellRef } from './computeCellRange';

/** 셀 클립보드 localStorage 키(실제 read/write 배선은 EditorPage 몫). */
export const CELL_CLIPBOARD_KEY = 'composer:cell-clipboard';

/**
 * 셀 full-fidelity 스냅샷 — 붙여넣기로 이식할 값+서식+구조를 화이트리스트로 담는다.
 *
 * 포함:
 *   - 서식: contentAlignH/V, backgroundKey, backgroundKeyAlpha, style
 *   - 내용: text, binding, compute, aggregate
 *   - 구조: rowspan, colspan (span 붙여넣기 적용은 best-effort — 그리드 무결성 우선)
 * 제외: 절대좌표(row/col) — grid 상대위치(dr/dc)로 표현하므로 스냅샷엔 담지 않는다.
 * `content`(중첩 노드 배열)도 담지 않는다(1차 범위 밖). structuredClone 깊은복사로 원본 참조 공유를 끊는다.
 */
export type CellSnapshot = Pick<
  TableCell,
  | 'contentAlignH'
  | 'contentAlignV'
  | 'backgroundKey'
  | 'backgroundKeyAlpha'
  | 'style'
  | 'text'
  | 'binding'
  | 'compute'
  | 'aggregate'
  | 'rowspan'
  | 'colspan'
>;

/**
 * 셀 클립보드 페이로드 — 값(TSV) + full-fidelity 스냅샷 grid를 함께 보관한다.
 *
 * tsv는 buildTsv 결과와 동일하며 붙여넣기 시 "내부 복사인지"를 판별하는 키로 쓴다(paste text와 일치하면 내부).
 * cells grid는 tsv와 같은 min~max 사각형으로 평행 정렬(같은 인덱싱)되며, 병합/부재 슬롯은 null.
 */
export interface CellClipboardData {
  /** 스키마 버전 — 방어 파싱 가드(현재 1) */
  version: 1;
  /** buildTsv 결과 — 외부/내부 판별용(붙여넣기 text와 일치하면 내부) */
  tsv: string;
  /** 사각형 범위 행 수 (maxRow-minRow+1) */
  rows: number;
  /** 사각형 범위 열 수 (maxCol-minCol+1) */
  cols: number;
  /** tsv grid와 평행(같은 min~max 사각형). 병합/부재 슬롯=null */
  cells: (CellSnapshot | null)[][];
}

/**
 * 선택 셀들의 사각형 범위(min~max row/col)를 TSV로 직렬화한다.
 *
 * - 열 구분=`\t`, 행 구분=`\n`. 각 슬롯의 cell.text(없으면 빈 문자열).
 * - 병합으로 덮인 슬롯(실제 cells에 없는 좌표)은 빈 문자열(엑셀과 동형).
 * - 단일 셀이면 그 text 한 줄.
 * - cells가 비면 빈 문자열.
 *
 * 주의: 선택이 직사각이 아니어도(병합 인지 선택 등) min~max 범위를 직사각으로 채워 출력한다
 * (엑셀도 비직사각 선택의 복사는 외곽 사각형으로 직렬화).
 */
export function buildTsv(table: TableNode, cells: CellRef[]): string {
  if (cells.length === 0) return '';
  let minRow = Infinity;
  let maxRow = -Infinity;
  let minCol = Infinity;
  let maxCol = -Infinity;
  for (const c of cells) {
    if (c.row < minRow) minRow = c.row;
    if (c.row > maxRow) maxRow = c.row;
    if (c.col < minCol) minCol = c.col;
    if (c.col > maxCol) maxCol = c.col;
  }
  const rows: string[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    const colTexts: string[] = [];
    for (let c = minCol; c <= maxCol; c++) {
      const cell = table.cells.find((cl) => cl.row === r && cl.col === c);
      colTexts.push(cell?.text ?? '');
    }
    rows.push(colTexts.join('\t'));
  }
  return rows.join('\n');
}

/**
 * TSV 문자열을 2D 문자열 배열(행 × 열)로 파싱한다.
 *
 * - 행 구분=`\n`(CRLF는 LF로 정규화), 열 구분=`\t`.
 * - 엑셀이 복사 끝에 붙이는 trailing newline 1개는 제거해 허위 빈 행을 막는다(아래 셀을 비우는 사고 방지).
 * - 빈 문자열이면 빈 배열.
 * - 1차 범위는 단순 split — 셀 내부 줄바꿈을 따옴표로 감싸는 엑셀 규약은 미지원(후속).
 */
export function parseTsv(text: string): string[][] {
  if (text === '') return [];
  const normalized = text.replace(/\r\n/g, '\n').replace(/\n$/, '');
  return normalized.split('\n').map((line) => line.split('\t'));
}

/**
 * 선택 셀들의 값(TSV)과 full-fidelity 스냅샷 grid를 클립보드 페이로드로 빌드한다.
 *
 * - cells가 비면 null.
 * - tsv는 buildTsv와 **동일한 min~max 사각형**으로 산출(text와 cells가 같은 인덱싱으로 정렬됨을 보장).
 * - 각 슬롯(i=r-minRow, j=c-minCol): 실제 cell이 있으면 화이트리스트 필드(서식+내용+구조)만 추출한 스냅샷,
 *   없으면(병합 덮인 슬롯 등) null. 값 없는 필드는 생략한다(undefined 미포함).
 * - 절대좌표(row/col)·content(중첩 노드)는 스냅샷에 담지 않는다.
 * - style/binding/compute/aggregate 등 객체 필드는 structuredClone 깊은복사로 원본 참조 공유를 끊는다
 *   (붙여넣기 후 원본/사본 상호 오염 방지).
 */
export function buildCellClipboard(table: TableNode, cells: CellRef[]): CellClipboardData | null {
  if (cells.length === 0) return null;
  let minRow = Infinity;
  let maxRow = -Infinity;
  let minCol = Infinity;
  let maxCol = -Infinity;
  for (const c of cells) {
    if (c.row < minRow) minRow = c.row;
    if (c.row > maxRow) maxRow = c.row;
    if (c.col < minCol) minCol = c.col;
    if (c.col > maxCol) maxCol = c.col;
  }
  const cellSnaps: (CellSnapshot | null)[][] = [];
  for (let r = minRow; r <= maxRow; r++) {
    const rowSnaps: (CellSnapshot | null)[] = [];
    for (let c = minCol; c <= maxCol; c++) {
      const cell = table.cells.find((cl) => cl.row === r && cl.col === c);
      if (!cell) {
        rowSnaps.push(null);
        continue;
      }
      const snap: CellSnapshot = {};
      // 서식(정렬/배경)
      if (cell.contentAlignH !== undefined) snap.contentAlignH = cell.contentAlignH;
      if (cell.contentAlignV !== undefined) snap.contentAlignV = cell.contentAlignV;
      if (cell.backgroundKey !== undefined) snap.backgroundKey = cell.backgroundKey;
      if (cell.backgroundKeyAlpha !== undefined) snap.backgroundKeyAlpha = cell.backgroundKeyAlpha;
      if (cell.style !== undefined) snap.style = cell.style;
      // 내용(text/binding/compute/aggregate)
      if (cell.text !== undefined) snap.text = cell.text;
      if (cell.binding !== undefined) snap.binding = cell.binding;
      if (cell.compute !== undefined) snap.compute = cell.compute;
      if (cell.aggregate !== undefined) snap.aggregate = cell.aggregate;
      // 구조(span) — 붙여넣기 적용은 best-effort(EditorPage)
      if (cell.rowspan !== undefined) snap.rowspan = cell.rowspan;
      if (cell.colspan !== undefined) snap.colspan = cell.colspan;
      // 객체 필드 참조 공유 차단 — 스냅샷 통째 깊은복사.
      rowSnaps.push(structuredClone(snap));
    }
    cellSnaps.push(rowSnaps);
  }
  return {
    version: 1,
    tsv: buildTsv(table, cells),
    rows: maxRow - minRow + 1,
    cols: maxCol - minCol + 1,
    cells: cellSnaps,
  };
}

/** 페이로드를 localStorage에 담을 문자열로 직렬화한다. */
export function serializeCellClipboard(data: CellClipboardData): string {
  return JSON.stringify(data);
}

/**
 * 직렬화 문자열을 페이로드로 역직렬화한다(방어적 검증 — nodeClipboard.parseNodeClipboard 패턴 미러).
 *
 * JSON.parse 실패, null/빈 입력, version!==1, cells가 배열이 아닌 경우 null을 반환한다.
 * tsv/rows/cols는 형식 관용(문자열/숫자 아니면 기본값 보정)하되 cells 배열 유무를 핵심 가드로 쓴다.
 */
export function parseCellClipboard(raw: string | null): CellClipboardData | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== 1) return null;
  if (!Array.isArray(obj.cells)) return null;
  return {
    version: 1,
    tsv: typeof obj.tsv === 'string' ? obj.tsv : '',
    rows: typeof obj.rows === 'number' ? obj.rows : obj.cells.length,
    cols: typeof obj.cols === 'number' ? obj.cols : (obj.cells[0]?.length ?? 0),
    cells: obj.cells as (CellSnapshot | null)[][],
  };
}
